import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, ExternalLink, AlertTriangle, Clock, HardDrive, FileText, Cpu, Eye } from 'lucide-react';
import type { ExtractionHistoryItem } from '../types/wiseboard';
import { ExtractionTextModal } from './ExtractionTextModal';

interface Props {
  item: ExtractionHistoryItem;
  onRetry?: (id: string) => void;
  onViewFile?: (storagePath: string) => void;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) {
return '—';
}
  if (bytes < 1024) {
return `${bytes} B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(1)} KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms: number | null): string {
  if (ms === null) {
return '—';
}
  if (ms < 1000) {
return `${ms}ms`;
}
  const seconds = ms / 1000;
  if (seconds < 60) {
return `${seconds.toFixed(1)}s`;
}
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

function formatNumber(num: number | null): string {
  if (num === null) {
return '—';
}
  return num.toLocaleString();
}

export function ExtractionDetailPanel({ item, onRetry, onViewFile }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isFailed = item.status === 'failed';
  const hasExtractedText = item.extracted_text && item.extracted_text.length > 0;

  return (
    <div className="p-4 space-y-4">
      {/* Error Message (if failed) */}
      {isFailed && item.error_message && (
        <Card className="p-3 border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
              <p className="text-sm text-red-700 dark:text-red-300">{item.error_message}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Timestamps */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Timestamps
          </h4>
          <dl className="space-y-1.5 text-sm">
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="font-medium">{format(parseISO(item.created_at), 'MMM d, yyyy h:mm:ss a')}</dd>
            </div>
            {item.started_at && (
              <div>
                <dt className="text-muted-foreground">Started</dt>
                <dd className="font-medium">{format(parseISO(item.started_at), 'MMM d, yyyy h:mm:ss a')}</dd>
              </div>
            )}
            {item.completed_at && (
              <div>
                <dt className="text-muted-foreground">Completed</dt>
                <dd className="font-medium">{format(parseISO(item.completed_at), 'MMM d, yyyy h:mm:ss a')}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* File Info */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <HardDrive className="h-3 w-3" />
            File Info
          </h4>
          <dl className="space-y-1.5 text-sm">
            <div>
              <dt className="text-muted-foreground">Size</dt>
              <dd className="font-medium">{formatBytes(item.file_size_bytes)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Source</dt>
              <dd>
                <Badge variant="outline" className="text-xs">
                  {item.source_type}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Retries</dt>
              <dd className="font-medium">{item.retry_count}/3</dd>
            </div>
          </dl>
        </div>

        {/* Extraction Metrics */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Extraction
          </h4>
          <dl className="space-y-1.5 text-sm">
            <div>
              <dt className="text-muted-foreground">Pages</dt>
              <dd className="font-medium text-blue-600">{formatNumber(item.total_pages)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Characters</dt>
              <dd className="font-medium text-purple-600">{formatNumber(item.extracted_char_count)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Duration</dt>
              <dd className="font-medium text-amber-600">{formatDuration(item.extraction_time_ms)}</dd>
            </div>
          </dl>
        </div>

        {/* Processing Info */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Cpu className="h-3 w-3" />
            Processing
          </h4>
          <dl className="space-y-1.5 text-sm">
            <div>
              <dt className="text-muted-foreground">GPU</dt>
              <dd className="font-medium">{item.processed_by_gpu?.toUpperCase() || '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Progress</dt>
              <dd className="font-medium">{item.progress}%</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Loan ID</dt>
              <dd className="font-mono text-xs truncate max-w-[150px]" title={item.encompass_loan_id}>
                {item.encompass_loan_id}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Extracted Text Preview (if available) */}
      {item.extracted_text && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Extracted Text Preview
          </h4>
          <Card className="p-3 bg-muted/50 max-h-32 overflow-y-auto">
            <pre className="text-xs font-mono whitespace-pre-wrap">
              {item.extracted_text.slice(0, 500)}
              {item.extracted_text.length > 500 && '...'}
            </pre>
          </Card>
        </div>
      )}

      {/* Classification Result (if available) */}
      {item.classification_result && Object.keys(item.classification_result).length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Classification Result
          </h4>
          <Card className="p-3 bg-muted/50">
            <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto">
              {JSON.stringify(item.classification_result, null, 2)}
            </pre>
          </Card>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-2">
        {hasExtractedText && (
          <Button
            variant="default"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
          >
            <Eye className="h-4 w-4 mr-1" />
            View Full Extraction
          </Button>
        )}
        {isFailed && onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRetry(item.id);
            }}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Retry
          </Button>
        )}
        {onViewFile && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewFile(item.storage_path);
            }}
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            View File
          </Button>
        )}
      </div>

      {/* Full Extraction Modal */}
      {hasExtractedText && (
        <ExtractionTextModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          extractedText={item.extracted_text!}
          documentTitle={item.document_title}
          storagePath={item.storage_path}
        />
      )}
    </div>
  );
}

export default ExtractionDetailPanel;
